import { Alert, Box, Button, Grid, Stack } from '@mui/material';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getContract, getContractOptions, getContractFile, updateContract, updateContractFile } from '../../../redux/slices/contracts';
import { useDispatch, useSelector } from '../../../redux/store';
import ContractInfoCard from './contract/ContractInfoCard';
import ContractSaveCard from './contract/ContractSaveCard';
import ContractTermsCard from './contract/ContractTermsCard';
import ContractFileCard from './contract/ContractFileCard';
import { CONTRACT_FILE_TYPES } from './contract/contractFileTypesEnum';
import { scrollSX } from './contractStyles';
import LoadingAnimation from '../../../reusable-components/loader/LoadingAnimation';

const statuses = ['active', 'terminated', 'pending-renew', 'previous-owner'];

const defaultValues = {
    id: 0,
    contractTypeId: null,
    facilityId: null,
    vendorId: null,
    accountNumber: '',
    amount: '',
    invoiceEvery: '',
    paymentTerms: '',
    startDate: null,
    endDate: null,
    renewalDate: null,
    autoRenew: '',
    answerOne: '',
    answerTwo: '',
    answerThree: '',
    answerFour: '',
    answerFive: '',
    previousOwnership: false,
    assumed: false,
    terminationClauseWithCause: '',
    terminationClauseWithoutCause: '',
    isTerminated: false,
    terminationEffectiveDate: null,
};

function getContractValues(contract) {
    const values = Object.fromEntries(Object.entries(defaultValues).map(([field, fallback]) => [field, contract[field] ?? fallback]));
    return {
        ...values,
    };
}

export default function ContractView() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id = '' } = useParams();
    const [searchParams] = useSearchParams();
    const statusFromQuery = searchParams.get('status');
    const backStatus = statuses.includes(statusFromQuery) ? statusFromQuery : 'active';

    const { isLoading, error, contractById = {}, contractOptions } = useSelector((state) => state.contracts || {});
    const methods = useForm({ defaultValues });

    const { formState: { isSubmitting } } = methods;
    methods.register('id');

    const contract = contractById?.[id];
    const [fileSelection, setFileSelection] = useState(null);
    const activeFileType = fileSelection?.id === id ? fileSelection.fileType : CONTRACT_FILE_TYPES.CONTRACT_LETTER;
    const activeFileName = activeFileType === CONTRACT_FILE_TYPES.CONTRACT_LETTER ? contract?.contractLetterFileName : contract?.terminationLetterFileName;

    const isEditable = (!contract && !!contractOptions?.facilities?.length || contract?.isEditable) && !isSubmitting;

    useEffect(() => {
        if (id && id !== 'new' && !contract) {
            dispatch(getContract(id));
        }
    }, [dispatch, id, contract]);

    useEffect(() => {
        if (id && activeFileName) {
            dispatch(getContractFile(id, activeFileType));
        }
    }, [dispatch, id, activeFileType, activeFileName]);

    useEffect(() => {
        if (!contractOptions?.facilities) {
            dispatch(getContractOptions());
        }
    }, [dispatch, contractOptions]);

    useEffect(() => {
        if (id === 'new') {
            methods.reset(defaultValues);
        }
    }, [id, methods]);

    useEffect(() => {
        if (!contract) {
            return;
        }
        // File uploads update Redux without replacing the user's unsaved form values.
        if (methods.getValues('id') === (contract.id)) {
            return;
        }
        methods.reset(getContractValues(contract));
    }, [contract, methods]);

    const onSubmit = async (values) => {
        const result = await dispatch(updateContract(values));
        if (!result?.success) {
            return;
        }
        methods.reset(getContractValues(result.data));
        if (id === 'new' && result.data.id > 0) {
            setIdInFormAndParams(result.data.id);
        }
    };

    const onUploadFile = async (file, fileType) => {
        const updatedContract = await dispatch(updateContractFile(file, methods.getValues('id'), fileType));
        if (methods.getValues('id') === 0) {
            setFileSelection({ id: String(updatedContract.id), fileType, });
            setIdInFormAndParams(updatedContract.id)
        }
    };

    const setIdInFormAndParams = (newId) => {
        methods.resetField('id', { defaultValue: newId });
        navigate(
            {
                pathname: `/dashboard/contracts/${newId}`,
                search: searchParams.toString(),
            },
            { replace: true },
        );
    }

    if (isLoading && !contract && id > 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <LoadingAnimation />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Button sx={{ width: 'fit-content' }} variant="outlined" onClick={() => navigate(`/dashboard/contracts/${backStatus}`)}>
                    Back to Contracts
                </Button>

                {!contract && (id !== 'new') ? (
                    <Alert severity={error ? 'error' : 'info'}>
                        {error ? error?.error?.message || 'Unable to load contract details.' : 'Contract not found.'}
                    </Alert>
                ) : (
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit(onSubmit)}>
                            <Grid container spacing={1.5} >
                                <Grid item xs={12} md={3} sx={{
                                    maxHeight: '70vh', overflow: 'auto',
                                    ...scrollSX,
                                    '&:hover': {
                                        scrollbarWidth: 'none',
                                    }
                                }} >
                                    <ContractSaveCard isEditable={isEditable} />
                                    <ContractInfoCard isEditable={isEditable} />
                                    <ContractTermsCard isEditable={isEditable} />
                                </Grid>
                                <Grid item xs={12} md={9} >
                                    <ContractFileCard
                                        key={id || 'new'}
                                        activeTab={activeFileType}
                                        onTabChange={(fileType) => setFileSelection({ id, fileType })}
                                        contractFile={contract?.contractFile}
                                        terminationFile={contract?.terminationFile}
                                        contractFileName={contract?.contractLetterFileName || 'Contract.pdf'}
                                        terminationFileName={contract?.terminationLetterFileName || 'Termination.pdf'}
                                        onUploadContract={(file) => onUploadFile(file, CONTRACT_FILE_TYPES.CONTRACT_LETTER)}
                                        onUploadTermination={(file) => onUploadFile(file, CONTRACT_FILE_TYPES.TERMINATION_LETTER)}
                                        isEditable={isEditable}
                                    />
                                </Grid>
                            </Grid>
                        </form>
                    </FormProvider>
                )}
            </Stack>
        </Box>
    );
}

